import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import authService from '../services/authService';
import { validateEmail, validatePassword, validatePasswordMatch } from '../utils/validation';
import { trackEvent } from '../utils/analytics';
import LanguageSwitcher from './LanguageSwitcher';
import './Auth.css';

function Register() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [emailExists, setEmailExists] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    trackEvent('register_submit');
    setError('');
    setEmailExists(false);

    const emailError = validateEmail(email, t);
    if (emailError) {
      trackEvent('register_failed', { reason: 'invalid_email_format' });
      setError(emailError);
      return;
    }

    const matchError = validatePasswordMatch(password, confirmPassword, t);
    if (matchError) {
      trackEvent('register_failed', { reason: 'password_mismatch' });
      setError(matchError);
      return;
    }

    const passwordError = validatePassword(password, t);
    if (passwordError) {
      trackEvent('register_failed', { reason: 'invalid_password' });
      setError(passwordError);
      return;
    }

    setLoading(true);

    try {
      await authService.register(email, password);
      trackEvent('register_success');
      sessionStorage.setItem('registration_email', email);
      navigate('/registration-success', {
        state: { email }
      });
    } catch (err) {
      const errorMessage = err.response?.data?.message;
      if (errorMessage === 'EMAIL_EXISTS') {
        trackEvent('register_failed', { reason: 'email_exists' });
        setEmailExists(true);
        setError(t('auth.register.emailExists'));
      } else {
        trackEvent('register_failed', { reason: 'server_error' });
        setError(errorMessage || t('auth.register.error'));
      }
      setLoading(false);
    }
  };

  const handleErrorClick = () => {
    if (emailExists) {
      navigate('/login');
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h2>{t('auth.register.title')}</h2>
          <LanguageSwitcher />
        </div>

        {error && (
          <div
            className={`error-message ${emailExists ? 'clickable' : ''}`}
            onClick={handleErrorClick}
            style={emailExists ? { cursor: 'pointer' } : {}}
          >
            {error}
            {emailExists && <span className="error-hint"> {t('auth.register.clickToLogin')}</span>}
          </div>
        )}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="email">{t('auth.register.email')}</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
              placeholder={t('auth.register.emailPlaceholder')}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">{t('auth.register.password')}</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
              placeholder={t('auth.register.passwordPlaceholder')}
              minLength="6"
            />
            <small className="password-hint">
              {t('auth.register.passwordHint')}
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">{t('auth.register.confirmPassword')}</label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              disabled={loading}
              placeholder={t('auth.register.confirmPasswordPlaceholder')}
              minLength="6"
            />
          </div>

          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? t('auth.register.submitting') : t('auth.register.submit')}
          </button>
        </form>

        <div className="auth-links">
          <Link to="/" className="link-button">
            {t('auth.register.toHome')}
          </Link>
          <Link to="/login" className="link-button">
            {t('auth.register.hasAccount')}
          </Link>
        </div>
      </div>
    </div>
  );
}

export default Register;
